import { useState } from "react";
import { yupResolver } from "@hookform/resolvers/yup";
import { Button, Dialog, DialogContent, Select } from "@jhool-io/fe-components";
import { Controller, useForm } from "react-hook-form";
import * as yup from "yup";
import ArrowUpRightIcon from "../../../../icons/ArrowUpRight";
import {
    calculateScreeningResult,
    formatScreeningResult,
    formatScreeningResponses,
    SCREENING_TOOLS,
} from "../../helpers/screeningTools.helpers";
import { ScreeningQuestion, ScreeningTool } from "../../types/notes.types";

interface ScreeningToolsProps {
    onComplete: (result: string) => void;
}

interface Option {
    label: string;
    value: string;
}

interface ScreeningToolsFormValues {
    responses: Record<string, string>;
}

const toolOptions = SCREENING_TOOLS.map(({ id, title }) => ({
    label: title,
    value: id,
}));
const defaultScreeningTool =
    SCREENING_TOOLS.find(({ id }) => id === "gad7") ?? SCREENING_TOOLS[0];

function getQuestionLabel(question: ScreeningQuestion) {
    const questionNumber = question.id.match(/^question_(\d+)$/)?.[1];

    if (!questionNumber || question.showNumber === false) {
        return question.label;
    }

    return `${questionNumber}. ${question.label}`;
}

export default function ScreeningTools({ onComplete }: ScreeningToolsProps) {
    const [isOpen, setIsOpen] = useState(false);
    const [selectedTool, setSelectedTool] = useState<ScreeningTool | null>(
        defaultScreeningTool
    );
    const {
        control,
        handleSubmit,
        reset,
        watch,
        formState: { errors },
    } = useForm<ScreeningToolsFormValues>({
        defaultValues: { responses: {} },
        mode: "onSubmit",
        reValidateMode: "onChange",
        resolver: yupResolver(
            yup.object({
                responses: yup
                    .object()
                    .shape(
                        Object.fromEntries(
                            (
                                selectedTool?.sections
                                    .flatMap(({ questions }) => questions)
                                    .filter(
                                        ({ required = true }) => required
                                    ) ?? []
                            ).map((question) => [
                                question.id,
                                yup.string().required("Response is required"),
                            ])
                        )
                    ),
            })
        ),
    });
    const responses = watch("responses") ?? {};

    const requiredQuestions = selectedTool?.sections.flatMap(({ questions }) =>
        questions.filter(({ required = true }) => required)
    );
    const answeredQuestionCount =
        requiredQuestions?.filter(({ id }) => responses[id]).length ?? 0;
    const requiredQuestionCount = requiredQuestions?.length ?? 0;
    const isComplete =
        requiredQuestionCount > 0 &&
        answeredQuestionCount === requiredQuestionCount;
    const screeningResult =
        selectedTool && isComplete
            ? calculateScreeningResult(selectedTool, responses)
            : null;

    const closeDialog = () => {
        setIsOpen(false);
        setSelectedTool(defaultScreeningTool);
        reset({ responses: {} });
    };

    const handleComplete = ({
        responses: submittedResponses,
    }: ScreeningToolsFormValues) => {
        if (!selectedTool) {
            return;
        }

        onComplete(formatScreeningResponses(selectedTool, submittedResponses));
        closeDialog();
    };

    return (
        <>
            <div className="mb-[--spacing-items]">
                <Button
                    type="button"
                    variant="secondary"
                    className="gap-x-1"
                    onClick={() => setIsOpen(true)}
                >
                    Add screening <ArrowUpRightIcon />
                </Button>
            </div>
            {isOpen ? (
                <Dialog open>
                    <DialogContent
                        title="Add screening"
                        variant="drawer"
                        wrapperClass="w-[790px]"
                        saveText="Submit"
                        handleCloseDialog={closeDialog}
                        onSaveClick={() => handleSubmit(handleComplete)()}
                    >
                        <div className="mb-8 flex flex-col gap-y-[--spacing-form-items]">
                            <h2 className="text-title font-semibold">
                                Add screening
                            </h2>
                            <div className="fg">
                                <Select
                                    isRequired
                                    label="Screening tool"
                                    placeholder="Select a screening tool"
                                    options={toolOptions}
                                    value={
                                        toolOptions.find(
                                            ({ value }) =>
                                                value === selectedTool?.id
                                        ) ?? null
                                    }
                                    onChange={(option) => {
                                        const toolId = (option as Option).value;

                                        reset({ responses: {} });
                                        setSelectedTool(
                                            SCREENING_TOOLS.find(
                                                ({ id }) => id === toolId
                                            ) ?? null
                                        );
                                    }}
                                />
                            </div>
                            {selectedTool ? (
                                <>
                                    <p className="-mt-4 text-base font-semibold">
                                        {selectedTool.description}
                                    </p>
                                    <p className="note-label">
                                        {answeredQuestionCount} of{" "}
                                        {requiredQuestionCount} required
                                        questions answered
                                    </p>
                                    {selectedTool.sections.map(
                                        (section, sectionIndex) => (
                                            <div
                                                key={
                                                    section.title ??
                                                    sectionIndex
                                                }
                                                className={
                                                    "flex flex-col " +
                                                    "gap-y-[--spacing-form-items]"
                                                }
                                            >
                                                {section.title ? (
                                                    <h3 className="note-header">
                                                        {section.title}
                                                    </h3>
                                                ) : null}
                                                {section.description ? (
                                                    <p className="text-base">
                                                        {section.description}
                                                    </p>
                                                ) : null}
                                                {section.questions.map(
                                                    (question) => {
                                                        const options =
                                                            question.options.map(
                                                                (option) => ({
                                                                    label: option,
                                                                    value: option,
                                                                })
                                                            );
                                                        const label =
                                                            getQuestionLabel(
                                                                question
                                                            );
                                                        const questionError =
                                                            errors.responses?.[
                                                                question.id
                                                            ];

                                                        return (
                                                            <div
                                                                key={
                                                                    question.id
                                                                }
                                                                className="fg"
                                                            >
                                                                <Controller
                                                                    name={`responses.${question.id}`}
                                                                    control={
                                                                        control
                                                                    }
                                                                    render={({
                                                                        field,
                                                                    }) => (
                                                                        <Select
                                                                            isRequired={
                                                                                question.required !==
                                                                                false
                                                                            }
                                                                            label={
                                                                                label
                                                                            }
                                                                            placeholder=""
                                                                            options={
                                                                                options
                                                                            }
                                                                            value={
                                                                                options.find(
                                                                                    ({
                                                                                        value,
                                                                                    }) =>
                                                                                        value ===
                                                                                        field.value
                                                                                ) ??
                                                                                null
                                                                            }
                                                                            onChange={(
                                                                                option
                                                                            ) =>
                                                                                field.onChange(
                                                                                    (
                                                                                        option as Option
                                                                                    )
                                                                                        .value
                                                                                )
                                                                            }
                                                                            hasError={
                                                                                !!questionError
                                                                            }
                                                                            errorText={
                                                                                questionError?.message
                                                                            }
                                                                        />
                                                                    )}
                                                                />
                                                            </div>
                                                        );
                                                    }
                                                )}
                                            </div>
                                        )
                                    )}
                                    {screeningResult ? (
                                        <div className="fg">
                                            <h3 className="note-header">
                                                Calculated result
                                            </h3>
                                            {formatScreeningResult(
                                                screeningResult
                                            ).map((line) => (
                                                <p
                                                    key={line}
                                                    className="text-base text-textSec"
                                                >
                                                    {line}
                                                </p>
                                            ))}
                                        </div>
                                    ) : null}
                                </>
                            ) : null}
                        </div>
                    </DialogContent>
                </Dialog>
            ) : null}
        </>
    );
}
